import { isOsanoCmpActiveForCurrentUser } from 'lib/osanoCmpScriptSrc'

// ---------------------------------------------------------------------------
// Additional event-name constants used by subscribeToOsanoConsentEvents
// ---------------------------------------------------------------------------
const OSANO_EVENT_UI_CHANGED = 'osano-cm-ui-changed'
const OSANO_EVENT_SCRIPT_BLOCKED = 'osano-cm-script-blocked'

export type OsanoConsentCategory = 'ACCEPT' | 'DENY'

export type OsanoConsent = {
  ANALYTICS?: OsanoConsentCategory
  MARKETING?: OsanoConsentCategory
  PERSONALIZATION?: OsanoConsentCategory
  ESSENTIAL?: OsanoConsentCategory
  STORAGE?: OsanoConsentCategory
  'OPT-OUT'?: OsanoConsentCategory
}

const OSANO_EVENT_INITIALIZED = 'osano-cm-initialized'
const OSANO_EVENT_CONSENT_SAVED = 'osano-cm-consent-saved'
const OSANO_EVENT_CONSENT_NEW = 'osano-cm-consent-new'
const OSANO_EVENT_ANALYTICS = 'osano-cm-analytics'

function isAnalyticsGrantedFromConsent(consent?: OsanoConsent): boolean {
  return consent?.ANALYTICS === 'ACCEPT'
}

export function isOsanoAnalyticsConsentGranted(): boolean {
  if (!isOsanoCmpActiveForCurrentUser()) {
    return true
  }

  return isAnalyticsGrantedFromConsent(window.Osano?.cm?.getConsent?.())
}

export function subscribeToOsanoAnalyticsConsent(
  onConsentChange: (granted: boolean) => void
): () => void {
  if (!isOsanoCmpActiveForCurrentUser()) {
    onConsentChange(true)

    return () => undefined
  }

  const cm = window.Osano?.cm

  if (!cm?.addEventListener || !cm?.removeEventListener) {
    onConsentChange(isOsanoAnalyticsConsentGranted())

    return () => undefined
  }

  const handleConsentObject = (consent?: OsanoConsent): void => {
    const analytics = consent?.ANALYTICS ?? cm.getConsent?.()?.ANALYTICS

    onConsentChange(analytics === 'ACCEPT')
  }

  const handleAnalyticsGranted = (): void => {
    onConsentChange(true)
  }

  cm.addEventListener(OSANO_EVENT_INITIALIZED, handleConsentObject)
  cm.addEventListener(OSANO_EVENT_CONSENT_SAVED, handleConsentObject)
  cm.addEventListener(OSANO_EVENT_CONSENT_NEW, handleConsentObject)
  cm.addEventListener(OSANO_EVENT_ANALYTICS, handleAnalyticsGranted)

  onConsentChange(isOsanoAnalyticsConsentGranted())

  return () => {
    cm.removeEventListener?.(OSANO_EVENT_INITIALIZED, handleConsentObject)
    cm.removeEventListener?.(OSANO_EVENT_CONSENT_SAVED, handleConsentObject)
    cm.removeEventListener?.(OSANO_EVENT_CONSENT_NEW, handleConsentObject)
    cm.removeEventListener?.(OSANO_EVENT_ANALYTICS, handleAnalyticsGranted)
  }
}

// ---------------------------------------------------------------------------
// Consent-event subscription — separate from the boolean analytics-consent
// subscription above so the two concerns remain decoupled.
// ---------------------------------------------------------------------------

export type OsanoConsentEventHandlers = {
  /** osano-cm-initialized: arg is undefined when no saved consent (banner shown) */
  onInitialized: (consent: OsanoConsent | undefined) => void
  /** osano-cm-consent-saved: fired when the visitor saves preferences */
  onConsentSaved: (consent: OsanoConsent) => void
  /** osano-cm-ui-changed: fires for any UI component show/hide */
  onUiChanged: (component: string, stateChange: string) => void
  /** osano-cm-script-blocked: fires each time a script is blocked */
  onScriptBlocked: (src: string) => void
}

/**
 * Subscribes to the four Osano events needed to emit the five RudderStack
 * consent events.  Returns a cleanup function.
 *
 * Does NOT duplicate the analytics-consent boolean subscription — callers must
 * use subscribeToOsanoAnalyticsConsent for that.
 *
 * @shared Used by:
 *   - hooks/useOsanoConsentEvents
 */
export function subscribeToOsanoConsentEvents(
  handlers: OsanoConsentEventHandlers
): () => void {
  if (!isOsanoCmpActiveForCurrentUser()) {
    return () => undefined
  }

  const cm = window.Osano?.cm

  if (!cm?.addEventListener || !cm?.removeEventListener) {
    return () => undefined
  }

  const handleInitialized = (consent: unknown): void => {
    handlers.onInitialized(consent as OsanoConsent | undefined)
  }

  const handleConsentSaved = (consent: unknown): void => {
    handlers.onConsentSaved(consent as OsanoConsent)
  }

  const handleUiChanged = (...args: unknown[]): void => {
    const [component, stateChange] = args as [string, string]
    handlers.onUiChanged(component, stateChange)
  }

  const handleScriptBlocked = (...args: unknown[]): void => {
    const [src] = args as [string]
    handlers.onScriptBlocked(src)
  }

  cm.addEventListener(OSANO_EVENT_INITIALIZED, handleInitialized)
  cm.addEventListener(OSANO_EVENT_CONSENT_SAVED, handleConsentSaved)
  cm.addEventListener(OSANO_EVENT_UI_CHANGED, handleUiChanged)
  cm.addEventListener(OSANO_EVENT_SCRIPT_BLOCKED, handleScriptBlocked)

  return () => {
    cm.removeEventListener?.(OSANO_EVENT_INITIALIZED, handleInitialized)
    cm.removeEventListener?.(OSANO_EVENT_CONSENT_SAVED, handleConsentSaved)
    cm.removeEventListener?.(OSANO_EVENT_UI_CHANGED, handleUiChanged)
    cm.removeEventListener?.(OSANO_EVENT_SCRIPT_BLOCKED, handleScriptBlocked)
  }
}
